import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TermsService {
  private termsCache: string | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Carga las condiciones de uso desde el archivo markdown
   */
  getTermsAsHtml(): Observable<string> {
    // Si ya tenemos las condiciones cacheadas, devolverlas
    if (this.termsCache) {
      return of(this.termsCache);
    }

    // Cargar el archivo markdown desde assets
    return this.http.get('assets/conditions/CONDICIONES_DE_USO.md', {
      responseType: 'text'
    }).pipe(
      map(markdown => {
        const html = this.markdownToHtml(markdown);
        this.termsCache = html;
        return html;
      }),
      catchError(error => {
        console.error('Error loading terms:', error);
        return of('<p>Error cargando las condiciones de uso.</p>');
      })
    );
  }

  /**
   * Convierte markdown simple a HTML de forma robusta
   */
  private markdownToHtml(markdown: string): string {
    let html = markdown;

    // Procesar headings ANTES de cualquier otra cosa (en orden descendente de nivel)
    html = html.replace(/^### (.*?)$/gm, '<h3>$1</h3>');
    html = html.replace(/^## (.*?)$/gm, '<h2>$1</h2>');
    html = html.replace(/^# (.*?)$/gm, '<h1>$1</h1>');

    // Procesar líneas horizontales
    html = html.replace(/^\s*---+\s*$/gm, '<hr>');
    html = html.replace(/^\s*===+\s*$/gm, '<hr>');

    // Procesar tablas (ANTES de listas para evitar conflictos)
    html = this.processTables(html);

    // Procesar listas
    html = this.processLists(html);

    // Procesar bold y italic
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.*?)__/g, '<strong>$1</strong>');
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    html = html.replace(/_(.*?)_/g, '<em>$1</em>');

    // Procesar párrafos
    html = this.processParagraphs(html);

    return html;
  }

  private processLists(html: string): string {
    // Procesar listas desordenadas
    const lines = html.split('\n');
    let result: string[] = [];
    let inList = false;
    let listType = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      // Detectar lista desordenada
      if (/^[-*+]\s+/.test(line)) {
        const content = line.replace(/^[-*+]\s+/, '').trim();

        if (!inList || listType !== 'ul') {
          if (inList) {
            result.push(`</${listType}>`);
          }
          result.push('<ul>');
          inList = true;
          listType = 'ul';
        }
        result.push(`<li>${content}</li>`);
      }
      // Detectar lista ordenada
      else if (/^\d+\.\s+/.test(line)) {
        const content = line.replace(/^\d+\.\s+/, '').trim();

        if (!inList || listType !== 'ol') {
          if (inList) {
            result.push(`</${listType}>`);
          }
          result.push('<ol>');
          inList = true;
          listType = 'ol';
        }
        result.push(`<li>${content}</li>`);
      } else {
        if (inList) {
          result.push(`</${listType}>`);
          inList = false;
          listType = '';
        }
        result.push(line);
      }
    }

    if (inList) {
      result.push(`</${listType}>`);
    }

    return result.join('\n');
  }

  private processTables(html: string): string {
    const lines = html.split('\n');
    const result: string[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i].trim();

      // Detectar inicio de tabla (línea que comienza y termina con |)
      if (line.startsWith('|') && line.endsWith('|')) {
        const tableLines: string[] = [];
        
        // Recopilar todas las líneas consecutivas de la tabla
        while (i < lines.length) {
          const currentLine = lines[i].trim();
          if (currentLine.startsWith('|') && currentLine.endsWith('|')) {
            tableLines.push(currentLine);
            i++;
          } else {
            break;
          }
        }

        // Procesar la tabla si tiene al menos encabezado + separador + 1 fila
        if (tableLines.length >= 2) {
          const tableHtml = this.convertTableToHtml(tableLines);
          result.push(tableHtml);
        }
      } else {
        result.push(line);
        i++;
      }
    }

    return result.join('\n');
  }

  private convertTableToHtml(tableLines: string[]): string {
    let html = '<table>\n';
    let inThead = false;
    let inTbody = false;

    for (let i = 0; i < tableLines.length; i++) {
      const line = tableLines[i];
      
      // Saltar la línea de separadores (|---|---|...)
      if (line.match(/^\|[\s\-|:]+\|$/)) {
        inTbody = true;
        continue;
      }

      // Extraer celdas (dividir por | y limpiar)
      const cells = line
        .split('|')
        .map(cell => cell.trim())
        .filter(cell => cell.length > 0);

      if (cells.length === 0) continue;

      // Primera fila (antes del separador) = encabezado
      if (i === 0 && !inTbody) {
        if (!inThead) {
          html += '<thead>\n';
          inThead = true;
        }
        html += '<tr>';
        cells.forEach(cell => {
          html += `<th>${cell}</th>`;
        });
        html += '</tr>\n';
      } else {
        // Resto = filas de datos
        if (inThead) {
          html += '</thead>\n';
          inThead = false;
        }
        if (!inTbody) {
          html += '<tbody>\n';
          inTbody = true;
        }
        html += '<tr>';
        cells.forEach(cell => {
          html += `<td>${cell}</td>`;
        });
        html += '</tr>\n';
      }
    }

    if (inTbody) {
      html += '</tbody>\n';
    }
    if (inThead) {
      html += '</thead>\n';
    }
    html += '</table>';
    return html;
  }

  private processParagraphs(html: string): string {
    const lines = html.split('\n');
    let result: string[] = [];
    let inBlock = false;

    for (const line of lines) {
      const trimmed = line.trim();

      // Si es una línea vacía o un elemento de bloque, cerrar párrafo si está abierto
      if (!trimmed || trimmed.match(/^<[^>]+>/) || trimmed.match(/<\/[^>]+>$/)) {
        if (inBlock && result[result.length - 1]?.startsWith('<p>')) {
          result[result.length - 1] += '</p>';
          inBlock = false;
        }
        if (trimmed) {
          result.push(trimmed);
        }
      } else {
        // Si no está en un bloque, abrir párrafo
        if (!inBlock) {
          result.push(`<p>${trimmed}`);
          inBlock = true;
        } else {
          result[result.length - 1] += ' ' + trimmed;
        }
      }
    }

    if (inBlock && result[result.length - 1]?.startsWith('<p>')) {
      result[result.length - 1] += '</p>';
    }

    return result.join('\n');
  }
}
